/**
 * Turns an architecture record into absolute pixel geometry.
 *
 * Pure on purpose: no React, no DOM, no measurement. Every position falls out
 * of the constants below, which is what makes the diagram testable without a
 * renderer and identical on every run.
 *
 * Layers are rows, drawn top to bottom. Edges may only join adjacent layers —
 * an edge that skipped one would have to run its horizontal segment straight
 * through the row in between. That rule lives in the data tests; here it is
 * simply assumed, which is what keeps the router to a single case.
 *
 * Layer captions live in a left-hand gutter rather than above each row: a
 * caption sitting above a row shares its vertical band with the wires
 * arriving from the layer above, which is exactly the busiest part of the
 * picture. The gutter is a column no wire ever crosses, so captions get it
 * for free. That is also why there is no longer a LABEL_H reserving space
 * above each row — a caption beside a row costs no extra height, only the
 * fixed GUTTER width once per diagram.
 */

export const GEOMETRY = Object.freeze({
  NODE_W: 104,
  NODE_H: 52,
  GAP_X: 12,   // between nodes within a layer
  GAP_Y: 56,   // between layers — also the room the elbows run in
  GUTTER: 64,  // left-hand column reserved for layer captions
  PAD: 20,
  RADIUS: 6,   // elbow corner
  MAX_PER_LAYER: 4,
  MAX_LABEL: 22,
});

const { NODE_W, NODE_H, GAP_X, GAP_Y, GUTTER, PAD, RADIUS } = GEOMETRY;

const rowWidth = (count) => count * NODE_W + Math.max(0, count - 1) * GAP_X;

// Coordinates land on halves at worst, but formatting them keeps the path
// strings stable and readable rather than exposing float noise.
const n = (value) => Number(value.toFixed(2));

function elbow(sx, sy, tx, ty) {
  if (sx === tx) return `M ${n(sx)} ${n(sy)} L ${n(tx)} ${n(ty)}`;

  const mid = (sy + ty) / 2;
  const dir = tx > sx ? 1 : -1;
  const r = Math.min(RADIUS, Math.abs(tx - sx) / 2, (ty - sy) / 2);

  return [
    `M ${n(sx)} ${n(sy)}`,
    `L ${n(sx)} ${n(mid - r)}`,
    `Q ${n(sx)} ${n(mid)} ${n(sx + dir * r)} ${n(mid)}`,
    `L ${n(tx - dir * r)} ${n(mid)}`,
    `Q ${n(tx)} ${n(mid)} ${n(tx)} ${n(mid + r)}`,
    `L ${n(tx)} ${n(ty)}`,
  ].join(' ');
}

export function architectureLayout(architecture) {
  const layers = architecture?.layers ?? [];
  const rawEdges = architecture?.edges ?? [];

  const widest = layers.reduce(
    (max, layer) => Math.max(max, rowWidth(layer.nodes.length)),
    0
  );
  const width = widest + PAD * 2 + GUTTER;
  const height =
    PAD * 2 +
    layers.length * NODE_H +
    Math.max(0, layers.length - 1) * GAP_Y;

  const nodes = [];
  const labels = [];

  layers.forEach((layer, layerIndex) => {
    const top = PAD + layerIndex * (NODE_H + GAP_Y);
    // The caption's box runs from the diagram's left edge to the end of the
    // gutter (x), right-aligned within it by the component, and centred on
    // the row's vertical midpoint (y) rather than pinned to its top.
    labels.push({ text: layer.label, x: PAD + GUTTER, y: top + NODE_H / 2 });

    const start = PAD + GUTTER + (widest - rowWidth(layer.nodes.length)) / 2;
    layer.nodes.forEach((node, nodeIndex) => {
      nodes.push({
        ...node,
        kind: node.kind || 'default',
        layer: layerIndex,
        x: start + nodeIndex * (NODE_W + GAP_X),
        y: top,
        w: NODE_W,
        h: NODE_H,
      });
    });
  });

  const byId = new Map(nodes.map((node) => [node.id, node]));

  const edges = rawEdges.map(([from, to, label]) => {
    const source = byId.get(from);
    const target = byId.get(to);
    const sx = source.x + source.w / 2;
    const sy = source.y + source.h;
    const tx = target.x + target.w / 2;
    const ty = target.y;

    return {
      from,
      to,
      label,
      d: elbow(sx, sy, tx, ty),
      labelX: n((sx + tx) / 2),
      labelY: n((sy + ty) / 2),
      // The layer a connection originates from — a wire is coloured by where
      // its data comes from, not where it's going. Geometry data, not
      // presentation: the component maps this index to an actual hue.
      sourceLayer: source.layer,
    };
  });

  return { width, height, nodes, labels, edges };
}
